import { isAuthenticatedSelector } from 'app/selectors';
import { AuditLogClientOperationEnum } from 'gql/graphql';
import { useProfileInfo } from 'hooks/useProfileInfo';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useSendLoginOperation } from 'hooks/useSendLoginOperation';
import keycloak from 'services/Keycloak';
import { Loader } from 'uikit';

import { ReactKeycloakProvider } from '@react-keycloak/web';
import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authSlice } from './authSlice';

export const AuthContainer = ({ children = null }: PropsWithChildren<{}>) => {
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(isAuthenticatedSelector);
    const { organisation, userName, role } = useProfileInfo();
    const { analyticsLoginEventManager, sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();
    const { sendLoginOperationHandler } = useSendLoginOperation();

    const [isKeycloakReady, setIsKeycloakReady] = useState(false);

    const handleKeyCloakEvent = useCallback((event) => {
        if (event === 'onReady') {
            setIsKeycloakReady(true);
        }
    }, []);

    useEffect(() => {
        if (keycloak.authenticated && organisation && analyticsLoginEventManager.checkIsNotDispatched()) {
            sendGpnAnalyticsEvent({
                componentId: 'user_login',
                constaeventtype: 'undefined',
                component: 'OTHER',
                customUserName: userName,
                customRole: role,
                customOrganisation: organisation,
            });
            sendLoginOperationHandler({
                operation: AuditLogClientOperationEnum.Login,
                userName,
            });
            analyticsLoginEventManager.saveStatus();
        }
    }, [analyticsLoginEventManager, organisation, role, sendGpnAnalyticsEvent, sendLoginOperationHandler, userName]);

    const handleKeyClockOnTokens = useCallback(
        (tokens) => {
            dispatch(authSlice.actions.setTokens(tokens));
        },
        [dispatch],
    );

    useEffect(() => {
        if (isKeycloakReady && !keycloak.authenticated) {
            keycloak.login();
            dispatch(authSlice.actions.initAuth());
            analyticsLoginEventManager.clearStatus();
        }
    }, [isKeycloakReady, dispatch, analyticsLoginEventManager]);

    return (
        <ReactKeycloakProvider
            authClient={keycloak}
            onEvent={handleKeyCloakEvent}
            onTokens={handleKeyClockOnTokens}
            LoadingComponent={<Loader size="m" />}
            initOptions={{ checkLoginIframe: false }}
        >
            {isAuthenticated ? children : <Loader size="m" />}
        </ReactKeycloakProvider>
    );
};

AuthContainer.displayName = 'AuthContainer';
